<script setup lang="ts">
import '@onenetwork/components/fileman/fileman.scss'
import { Icon } from '@iconify/vue'
import { computed, onMounted, ref, toRaw, watch } from 'vue'
import FolderItem from '@/onenetwork/components/fileman/FolderItem.vue'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import FileItem from '@/onenetwork/components/fileman/FileItem.vue'
import { config } from './config.ts'
import { PerfectScrollbar } from 'vue3-perfect-scrollbar'
import { uuid } from 'vue3-uuid'
import { useToast } from 'primevue/usetoast'

const toast = useToast()

interface FileSystemItem {
  type: 'd' | 'f'
  path: string
  [key: string]: any
}

const props = defineProps({
  picker: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['close', 'chooseFile'])

const menu = ref()
const folderMenu = ref()
const fileMenu = ref()
const isDragging = ref(false)
const loading = ref(false)

const scanPath = ref('uploads')
const selected = ref<FileSystemItem>({} as FileSystemItem)
const renameObj = ref<FileSystemItem>({} as FileSystemItem)

const isOpen = computed(() => {
  return !!props.picker?.uuid
})

const items = ref([
  {
    label: 'Tạo thư mục con',
    icon: 'pi pi-copy',
    command: () => {
      handleCreateFolder({
        path: scanPath.value,
        name: 'New Folder',
      })
    },
  },
  {
    label: 'Làm mới',
    icon: 'pi pi-file-edit',
    command: () => {
      fetchScanFolder()
    },
  },
])

const folderMenuItems = ref([
  {
    label: 'Vào thư mục',
    icon: 'pi pi-copy',
    command: () => {
      if (selected.value.type === 'd') {
        handleGoto(null, selected.value)
      }
    },
  },
  {
    label: 'Đổi tên',
    icon: 'pi pi-file-edit',
    command: () => {
      renameObj.value = { ...toRaw(selected.value) }
    },
  },
  {
    label: 'Xóa',
    icon: 'pi pi-file-edit',
    command: () => {
      handleDelete(selected.value)
    },
  },
])

const fileMenuItems = ref([
  {
    label: 'Chọn file',
    icon: 'pi pi-copy',
    command: () => {
      handleChooseFile()
    },
  },
  {
    label: 'Đổi tên',
    icon: 'pi pi-file-edit',
    command: () => {
      renameObj.value = { ...toRaw(selected.value) }
    },
  },
  {
    label: 'Xóa',
    icon: 'pi pi-file-edit',
    command: () => {
      handleDelete(selected.value)
    },
  },
])

const onPanelRightClick = (event: MouseEvent) => {
  menu.value.show(event, 1)
  folderMenu.value.hide()
  fileMenu.value.hide()
}

const onFolderRightClick = (event: MouseEvent, item: FileSystemItem) => {
  selected.value = item
  folderMenu.value.show(event)
  menu.value.hide()
  fileMenu.value.hide()
}

const onFileRightClick = (event: MouseEvent, item: FileSystemItem) => {
  selected.value = item
  fileMenu.value.show(event)
  menu.value.hide()
  folderMenu.value.hide()
}

const folderData = ref<FileSystemItem[]>([])

const fetchScanFolder = async () => {
  try {
    const res = await APIClient.get('file-management/list', {
      params: {
        path: scanPath.value,
        sort: 'asc',
      },
    })

    folderData.value = res.data.data.items
  } catch (e) {}
}

const handleSelect = (event: MouseEvent, item: FileSystemItem) => {
  selected.value = item
}

const handleGoto = (event: MouseEvent, item: FileSystemItem) => {
  scanPath.value = item.path
  selected.value = {} as FileSystemItem
  renameObj.value = {} as FileSystemItem
}

const handleHome = () => {
  scanPath.value = 'uploads'
  selected.value = {} as FileSystemItem
  renameObj.value = {} as FileSystemItem
}

const clearSelection = () => {
  selected.value = {} as FileSystemItem
  renameObj.value = {} as FileSystemItem
}

const handleGoBack = () => {
  scanPath.value = scanPath.value.split('/').slice(0, -1).join('/')
  selected.value = {} as FileSystemItem
  renameObj.value = {} as FileSystemItem
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = true
}

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false
}

const handleDrop = async (event: DragEvent) => {
  event.preventDefault()
  isDragging.value = false

  if (!event.dataTransfer?.files.length) return

  handleUpload(event.dataTransfer.files[0])
}

const fileInput = ref<HTMLInputElement | null>(null)

const handleClickUpload = () => {
  fileInput.value?.click()
}

const handleFileChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files?.length) return

  handleUpload(input.files[0])
}

const handleUpload = async (file: File) => {
  const formData = new FormData()
  formData.append('files', file)
  formData.append('path', scanPath.value)
  loading.value = true
  try {
    await api.post('file-management/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    fetchScanFolder()
    toast.add({
      severity: 'success',
      summary: 'Thành công',
      detail: 'Upload file thành công',
    })
  } catch (error) {
    console.error('Upload failed:', error?.response?.data?.message)
    toast.add({
      severity: 'error',
      summary: 'Lỗi',
      detail: 'Upload file thất bại',
    })
  }
}

const handleRename = async () => {
  selected.value = {} as FileSystemItem
  renameObj.value = {} as FileSystemItem
  fetchScanFolder()
}

const handleDelete = async (item: any) => {
  try {
    await APIClient.post('file-management/delete', {
      path: item.path,
    })
    fetchScanFolder()
  } catch (error) {
    console.error('Rename failed:', error)
  }
}

const handleCreateFolder = async (item: any) => {
  try {
    await APIClient.post('file-management/create-folder', {
      path: item.path,
      name: item.name,
    })
    fetchScanFolder()
  } catch (error) {
    console.error('Create folder failed:', error)
  }
}

const supportedImageTypes = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg']
const supportedVideoTypes = ['.mp4', '.webm', '.ogg', '.mov', '.avi']

const isImageFile = (path: string) => {
  return supportedImageTypes.some((type) => path.toLowerCase().endsWith(type))
}

const isVideoFile = (path: string) => {
  return supportedVideoTypes.some((type) => path.toLowerCase().endsWith(type))
}

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const formatDate = (date: string) => {
  return new Date(date).toLocaleString()
}

const getFileType = (path: string) => {
  if (isImageFile(path)) return 'Hình ảnh'
  if (isVideoFile(path)) return 'Video'
  const ext = path.split('.').pop()?.toUpperCase()
  return ext || 'Unknown'
}

const handleChooseFile = () => {
  emit('chooseFile', { picker: toRaw(props.picker), file: toRaw(selected.value) })
}

const handleSelectAndChoose = (event: MouseEvent, item: FileSystemItem) => {
  emit('chooseFile', { picker: toRaw(props.picker), file: toRaw(item) })
}

onMounted(() => {
  fetchScanFolder()
})

watch(scanPath, () => {
  fetchScanFolder()
})

watch(isOpen, () => {
  if (isOpen.value) {
    selected.value = {} as FileSystemItem
    renameObj.value = {} as FileSystemItem
    scanPath.value = 'uploads'
    fetchScanFolder()
  }
})
</script>

<template>
  <template v-if="isOpen">
    <div class="fileman">
      <div class="fileman__header">
        <h5>File Manager</h5>
        <div class="close-btn" @click="$emit('close')">
          <Icon icon="material-symbols-light:close" height="18px" /> Đóng
        </div>
      </div>
      <div class="fileman__toolbar">
        <button
          type="button"
          class="toolbar-item"
          @click="handleGoBack"
          :disabled="scanPath === 'uploads'"
        >
          <Icon icon="fluent-mdl2:chrome-back" /> <span>Trở lại</span>
        </button>
        <button class="toolbar-item" @click="handleHome">
          <Icon icon="material-symbols:home-rounded" /> <span>Mục gốc</span>
        </button>
      </div>
      <div class="fileman__content">
        <div class="directory" @click="clearSelection" @contextmenu="onPanelRightClick">
          <template v-for="(item, index) in folderData">
            <folder-item
              v-if="item.type === 'd'"
              @contextmenu="(e) => onFolderRightClick(e, item)"
              @click.stop="handleSelect($event, item)"
              @dbclick.stop="handleGoto($event, item)"
              :selected="selected"
              :rename-obj="renameObj"
              :item="item"
              :key="item.path"
              @rename-success="handleRename"
            />
            <file-item
              v-if="item.type === 'f'"
              @contextmenu="(e) => onFileRightClick(e, item)"
              @click.stop="handleSelect($event, item)"
              @dbclick.stop="handleSelectAndChoose($event, item)"
              :selected="selected"
              :rename-obj="renameObj"
              :item="item"
              :key="item.path"
              @rename-success="handleRename"
            />
          </template>
        </div>
        <div class="info" v-if="!selected.type || selected.type === 'd'">
          <PerfectScrollbar
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
            :class="{ 'is-dragging': isDragging }"
          >
            <div class="w-[100%] h-[100%] p-[16px]">
              <div class="mb-5">
                Đường dẫn thư mục: <br /><code
                  >/{{ selected.path ? selected.path : scanPath }}</code
                >
              </div>
              <div class="drop-zone" @click="handleClickUpload">
                <Icon icon="material-symbols:upload-file" height="50px" />
                <span>Click hoặc kéo thả file để upload</span>
              </div>
              <input
                ref="fileInput"
                type="file"
                multiple
                style="display: none"
                @change="handleFileChange"
              />
            </div>
          </PerfectScrollbar>
        </div>
        <div class="info" v-if="selected.type && selected.type === 'f'">
          <PerfectScrollbar>
            <div class="p-[16px] flex gap-2">
              <button class="btn btn-primary" @click="handleChooseFile">Chọn file</button>
              <button class="btn btn-secondary">Chỉnh sửa</button>
            </div>

            <div class="file-info p-[16px]">
              <div class="info-item">
                <span class="label">Tên file:</span>
                <span class="value">{{ selected.name }}</span>
              </div>
              <div class="info-item">
                <span class="label">Đường dẫn:</span>
                <span class="value">{{ selected.url }}</span>
              </div>
              <div class="info-item">
                <span class="label">Kích thước:</span>
                <span class="value">{{ formatFileSize(selected.size) }}</span>
              </div>
              <div class="info-item">
                <span class="label">Loại file:</span>
                <span class="value">{{ getFileType(selected.path) }}</span>
              </div>
              <div class="info-item">
                <span class="label">Ngày tạo:</span>
                <span class="value">{{ formatDate(selected.created_at) }}</span>
              </div>
            </div>
            <div
              class="preview-image p-[16px]"
              v-if="selected.type === 'f' && isImageFile(selected.path)"
            >
              <img :src="`${config.fileBaseUrl}/${selected.path}`" />
            </div>
            <div
              class="preview-video p-[16px]"
              v-if="selected.type === 'f' && isVideoFile(selected.path)"
            >
              <video :src="`${config.fileBaseUrl}/${selected.path}`" controls />
            </div>
          </PerfectScrollbar>
        </div>
      </div>
      <ContextMenu ref="menu" :model="items" />
      <ContextMenu ref="folderMenu" :model="folderMenuItems" />
      <ContextMenu ref="fileMenu" :model="fileMenuItems" />
    </div>
  </template>
</template>

<style scoped></style>
